import { useMotionValue } from 'motion/react';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Simulated-seconds clock for the race. One requestAnimationFrame loop
 * accumulates real deltas × the current speed multiplier into a MotionValue,
 * so playheads bind with useTransform and never trigger React renders.
 * Accumulating deltas (rather than `(now − t0) × speed`) makes mid-play
 * speed changes seamless.
 */
export function useSimClock(totalSeconds: number, onComplete: () => void) {
  const simTime = useMotionValue(0);
  const speedRef = useRef(60);
  const rafRef = useRef(0);
  const runningRef = useRef(false);
  const lastFrameRef = useRef(0);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const stop = useCallback(() => {
    runningRef.current = false;
    cancelAnimationFrame(rafRef.current);
  }, []);

  const start = useCallback(() => {
    if (runningRef.current) return;
    runningRef.current = true;
    lastFrameRef.current = performance.now();

    const tick = (now: number) => {
      if (!runningRef.current) return;
      const deltaSeconds = (now - lastFrameRef.current) / 1000;
      lastFrameRef.current = now;

      const next = Math.min(
        totalSeconds,
        simTime.get() + deltaSeconds * speedRef.current
      );
      simTime.set(next);

      if (next >= totalSeconds) {
        runningRef.current = false;
        onCompleteRef.current();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [totalSeconds, simTime]);

  const seek = useCallback(
    (seconds: number) => {
      simTime.set(Math.min(totalSeconds, Math.max(0, seconds)));
    },
    [simTime, totalSeconds]
  );

  useEffect(() => stop, [stop]);

  return { simTime, speedRef, start, stop, seek };
}
